import { LitElement, html } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-adress.style.js';
import { ApplicationService } from '../../../services/application.service.js';
import '@cof/fs-radio-wc/fs-radio.js';
import './address/address.component.js';
import { globalStyle } from '../../../global-styles.style.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import { stratum } from '../../../../constants/stratum.js';

class RefiStepAddressComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepAddress');
    this.validity = {};
    this.sameAddressMissing = false;
    this.sameAddress = undefined;
    this.isIconic = false;
    this.addressChildId = 'address-input';
  }

  static properties = {
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    sameAddress: { type: Boolean, reflect: true },
    sameAddressMissing: { type: Boolean, reflect: true },
    isIconic: { type: Boolean, attribute: 'is-iconic', reflect: true },
  };

  get application() {
    return ApplicationService.Application;
  }

  get applicant() {
    return this.isPrimary
      ? ApplicationService.Application.primaryApplicant
      : ApplicationService.Application.coApplicant;
  }
  static styles = [style, globalStyle];

  connectedCallback() {
    super.connectedCallback();
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.sameAddress =
      !this.isPrimary && typeof this.applicant.isSameAddress === 'boolean'
        ? this.applicant.isSameAddress
        : this.sameAddress;
    stratum.publishTag(
      this.isPrimary
        ? TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_ADDRESS
        : TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_CO_APP_ADDRESS
    );
  }

  render() {
    return html`
      <refi-heading ?is-iconic="${this.isIconic}">
        ${this.isIconic
          ? html` ${this.isPrefill
              ? html`Is this address still right?`
              : html`We need your address`}`
          : this.isPrimary
            ? html` ${this.isPrefill
                ? html`Great! Please confirm your address`
                : html`Great! Now tell us where you live`}`
            : html` Is ${this.applicant.firstName}’s address the same as the
              primary applicant?`}
      </refi-heading>
      <form
        id="step-same-address-form"
        class="step-same-address-form"
        @submit="${this.onSubmit}"
        novalidate
      >
        <div
          role="radiogroup"
          aria-labelledby="toggleSameAddress"
          ?hidden="${this.isPrimary}"
        >
          <fs-radio
            id="toggleSameAddressYes"
            name="toggleSameAddressYes"
            value="true"
            required
            ?checked="${this.sameAddress}"
            @change="${() => {
              this.sameAddress = true;
              this.validity = [];
              this.sameAddressMissing = false;
            }}"
            >Yes
          </fs-radio>
          <fs-radio
            id="toggleSameAddressNo"
            name="toggleSameAddressNo"
            value="false"
            required
            ?checked="${this.sameAddress === false}"
            @change="${() => {
              this.sameAddress = false;
              this.sameAddressMissing = false;
              document.activeElement?.blur();
            }}"
            >No
          </fs-radio>
          <fs-body-text
            size="small"
            variant="feedback-negative"
            ?hidden="${!this.sameAddressMissing}"
            id="sameAddressToggleInvalid"
          >
            Please select an option
          </fs-body-text>
        </div>
      </form>

      <form
        id="step-address-form"
        class="step-address-form"
        @submit="${this.onSubmit}"
        novalidate
      >
        <refi-personal-address
          id="${this.addressChildId}"
          ?active="${this.isPrimary ||
          (!this.isPrimary &&
            !this.sameAddress &&
            this.sameAddress !== undefined)}"
          @validity="${this.updateValidity}"
          ?is-primary="${this.isPrimary}"
          ?is-iconic="${this.isIconic}"
        ></refi-personal-address>

        <div
          class=${this.isIconic
            ? 'button-container-iconic'
            : 'button-container'}
        >
          <fs-button
            class=${this.isIconic
              ? 'fs-button-iconic next-button'
              : 'fs-button next-button'}
            variant="primary"
            @click="${this.checkOwnerStatus}"
            ><span>
              ${this.isIconic
                ? 'Confirm your address'
                : this.isPrefill
                  ? 'Confirm'
                  : 'Next'}</span
            >
          </fs-button>
        </div>
      </form>
    `;
  }

  checkOwnerStatus() {
    this.addressChild = this.shadowRoot.getElementById(this.addressChildId);
    if (this.addressChild) {
      this.runChildrenOwnerStatusCheck();
    }
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  @validateForm()
  async onSubmit() {
    this.sameAddressMissing = !this.isPrimary && this.sameAddress === undefined;
    this.updateValidity({
      detail: { name: 'sameAddress', isValid: !this.sameAddressMissing },
    });

    this.addressChild = this.shadowRoot.getElementById(this.addressChildId);
    if (this.isPrimary || !this.sameAddress) {
      this.runChildrenBusinessChecks();
      this.runChildrenOwnerStatusCheck();
    }

    this.debug.log('valid values: ', this.validity);
    let valid = true;
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });

    if (valid) {
      this.oldApplicantAddressInfo = structuredClone(
        this.applicant.currentAddress
      );

      const updatedApplication = structuredClone(this.application);
      const applicantType = this.isPrimary ? 'primaryApplicant' : 'coApplicant';

      if (!this.isPrimary) {
        updatedApplication[applicantType].isSameAddress = this.sameAddress;
      }

      if (this.addressChild) {
        Object.keys(this.addressChild.values).forEach((field) => {
          updatedApplication[applicantType].currentAddress[field] =
            this.addressChild.values[field];
        });
      }

      if (!this.isPrimary && this.sameAddress) {
        Object.assign(
          updatedApplication.coApplicant.currentAddress,
          updatedApplication.primaryApplicant.currentAddress
        );
      }

      ApplicationService.Application = updatedApplication;

      this.tagFieldChangesOnSubmit();
      this.debug.log('valid', this.application);
      this.dispatchEvent(new CustomEvent('next-step'));
    }
  }

  tagFieldChangesOnSubmit() {
    Object.keys(this.applicant.currentAddress).forEach((field) => {
      if (
        this.applicant.currentAddress[field] !==
        this.oldApplicantAddressInfo[field]
      ) {
        this.debug.log(`${this.coAppPrefix}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.coAppPrefix}${field}`,
          },
        });
      }
    });
  }

  runChildrenBusinessChecks() {
    this.addressChild.runBusinessChecks();
  }

  runChildrenOwnerStatusCheck() {
    this.addressChild.checkOwnerStatus();
  }
}

customElements.define('refi-step-address', RefiStepAddressComponent);
